import React, { useState, useEffect, useMemo } from 'react';
import { Table, Alert, Spinner, Badge, Button, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchProcessedBankStatements, fetchCaseCheckImages } from '../../api/cases';
import { formatCurrency } from '../../utils/formatters';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  ColumnFiltersState,
  SortingState,
  FilterFn,
  PaginationState,
} from '@tanstack/react-table';

interface BankStatement {
  statement_id: string;
  file_id: string;
  filename?: string;
  page_number: number;
  page_numbers?: number[];
  bank_name: string;
  account_number: string;
  statement_date: string;
  beginning_balance: number;
  total_credits: number;
  total_debits: number;
  ending_balance: number;
  created_at: string;
}

interface CheckImageType {
  check_image_id?: string;
  image_id?: string;
  file_id: string;
  filename?: string;
  page_number: number;
  bank_name?: string;
  payee?: string;
  date?: string;
  account_number?: string;
  amount?: string;
  extracted_date?: string;
  created_at?: string;
}

interface UnifiedDocument {
  id: string;
  file_id: string;
  page: number;
  pageDisplay?: string;  // Formatted page display (e.g., "1, 2, 3")
  document_type: 'bank_statement' | 'check_image';
  from_name?: string;
  to_name?: string;
  date?: string;
  account?: string;
  beginning_balance?: number;
  additions?: number;
  deductions?: number;
  ending_balance?: number;
  amount?: string;
  extracted_date?: string;
  source_data: any;
  file_name: string;
}

interface DocumentsTableProps {
  caseId: string;
}

// Extend ColumnMeta to include our custom properties
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends unknown, TValue> {
    filterComponent?: (column: any) => React.ReactNode;
  }
}

// Create a column helper
const columnHelper = createColumnHelper<UnifiedDocument>();

// Custom filter function for number ranges
const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  const [min, max] = value || [null, null];
  const rowValue = row.getValue(columnId);
  if (rowValue == null) return true;
  if (min != null && rowValue < min) return false;
  if (max != null && rowValue > max) return false;
  return true;
};

// Custom filter component for number ranges
const NumberRangeFilter = ({ column }: { column: any }) => {
  const [min, max] = column.getFilterValue() || [];
  
  return (
    <div className="d-flex gap-1">
      <Form.Control
        size="sm"
        type="number"
        placeholder="Min"
        value={min || ''}
        onChange={(e) => {
          const val = e.target.value;
          column.setFilterValue([val ? Number(val) : null, max]);
        }}
        style={{ width: '70px' }}
      />
      <Form.Control
        size="sm"
        type="number"
        placeholder="Max"
        value={max || ''}
        onChange={(e) => {
          const val = e.target.value;
          column.setFilterValue([min, val ? Number(val) : null]);
        }}
        style={{ width: '70px' }}
      />
    </div>
  );
};

// Custom filter component for select dropdowns
const SelectFilter = ({ column, options }: { column: any; options: string[] }) => {
  return (
    <Form.Select
      size="sm"
      value={column.getFilterValue() || ''}
      onChange={(e) => column.setFilterValue(e.target.value || undefined)}
    >
      <option value="">All</option>
      {options.map(option => (
        <option key={option} value={option}>{option}</option>
      ))}
    </Form.Select>
  );
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({ caseId }) => {
  const [documents, setDocuments] = useState<UnifiedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const { getAccessToken } = useAuth();

  // Get unique values for select filters
  const uniqueBankNames = useMemo(() => 
    [...new Set(documents.map(d => d.from_name).filter(Boolean))].sort(),
    [documents]
  );

  // Define columns with TanStack Table
  const columns = useMemo(() => [
    columnHelper.accessor('file_name', {
      header: 'File',
      cell: info => (
        <div className="small text-truncate" style={{ maxWidth: '120px' }} title={info.getValue()}>
          {info.getValue()}
        </div>
      ),
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.accessor('page', {
      header: 'Page #',
      cell: info => {
        const pageDisplay = info.row.original.pageDisplay;
        return pageDisplay || info.getValue();
      },
      enableColumnFilter: false,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('document_type', {
      header: 'Type',
      cell: info => (
        <Badge bg={info.getValue() === 'bank_statement' ? 'primary' : 'success'}>
          {info.getValue() === 'bank_statement' ? 'Statement' : 'Check'}
        </Badge>
      ),
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'equals',
      meta: {
        filterComponent: (column: any) => (
          <SelectFilter 
            column={column} 
            options={['bank_statement', 'check_image']} 
          />
        ),
      },
    }),
    columnHelper.accessor('from_name', {
      header: 'From',
      cell: info => <span className="small">{info.getValue() || '-'}</span>,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.accessor('to_name', {
      header: 'To',
      cell: info => <span className="small">{info.getValue() || '-'}</span>,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.accessor('date', {
      header: 'Date',
      cell: info => <span className="small">{info.getValue() || '-'}</span>,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.accessor('account', {
      header: 'Account',
      cell: info => <span className="small">{info.getValue() || '-'}</span>,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.accessor('beginning_balance', {
      header: 'Beginning',
      cell: info => {
        const value = info.getValue();
        return (
          <span className="small">
            {value !== undefined ? formatCurrency(value as number) : '-'}
          </span>
        );
      },
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: numberRangeFilter,
      meta: {
        filterComponent: (column: any) => <NumberRangeFilter column={column} />,
      },
    }),
    columnHelper.accessor('additions', {
      header: 'Additions',
      cell: info => {
        const value = info.getValue();
        return (
          <span className="small">
            {value !== undefined ? formatCurrency(value as number) : '-'}
          </span>
        );
      },
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: numberRangeFilter,
      meta: {
        filterComponent: (column: any) => <NumberRangeFilter column={column} />,
      },
    }),
    columnHelper.accessor('deductions', {
      header: 'Deductions',
      cell: info => {
        const value = info.getValue();
        return (
          <span className="small">
            {value !== undefined ? formatCurrency(value as number) : '-'}
          </span>
        );
      },
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: numberRangeFilter,
      meta: {
        filterComponent: (column: any) => <NumberRangeFilter column={column} />,
      },
    }),
    columnHelper.accessor('ending_balance', {
      header: 'Ending',
      cell: info => {
        const value = info.getValue();
        return (
          <span className="small">
            {value !== undefined ? formatCurrency(value as number) : '-'}
          </span>
        );
      },
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: numberRangeFilter,
      meta: {
        filterComponent: (column: any) => <NumberRangeFilter column={column} />,
      },
    }),
    columnHelper.accessor('amount', {
      header: 'Amount',
      cell: info => {
        const value = info.getValue();
        return (
          <span className="small">
            {value ? (value.startsWith('$') ? value : `$${value}`) : '-'}
          </span>
        );
      },
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue(columnId) as string;
        if (!value) return false;
        
        // Remove $ and commas to get the numeric value
        const numericValue = value.replace(/[$,]/g, '');
        const searchTerm = filterValue.replace(/[$,]/g, '');
        
        // Check if the numeric string includes the search term
        return numericValue.includes(searchTerm);
      },
      meta: {
        filterComponent: (column: any) => (
          <Form.Control
            size="sm"
            type="text"
            placeholder="Filter amount..."
            value={(column.getFilterValue() as string) ?? ''}
            onChange={(e) => column.setFilterValue(e.target.value)}
          />
        ),
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: info => (
        <Link to={`/case/${caseId}/file/${info.row.original.file_id}`}>
          <Button variant="outline-primary" size="sm">
            View
          </Button>
        </Link>
      ),
      enableColumnFilter: false,
    }),
  ], [caseId, uniqueBankNames]);

  // Create the table instance
  const table = useReactTable({
    data: documents,
    columns,
    state: {
      columnFilters,
      globalFilter,
      sorting,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableGlobalFilter: true,
    getColumnCanGlobalFilter: (column) => {
      // Allow ALL columns to be globally filtered, ignoring the default type check
      return column.columnDef.enableGlobalFilter !== false;
    },
    globalFilterFn: 'includesString', // Use built-in filter that works across all columns
    filterFns: {
      numberRangeFilter,
    },
  });

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessToken();
        const allDocuments: UnifiedDocument[] = [];

        // Fetch bank statements and check images in parallel — each lambda now joins filenames server-side
        const [statementsResult, checkImagesResult] = await Promise.allSettled([
          fetchProcessedBankStatements(caseId, token),
          fetchCaseCheckImages(caseId, token),
        ]);

        if (statementsResult.status === 'fulfilled') {
          const statements: BankStatement[] = statementsResult.value.data.statements || [];
          statements.forEach((statement) => {
            const pageNumbers = statement.page_numbers || (statement.page_number ? [statement.page_number] : [1]);
            const primaryPage = pageNumbers[0] || 1;
            const pageDisplay = pageNumbers.length > 1 ? pageNumbers.join(', ') : primaryPage.toString();
            allDocuments.push({
              id: `statement_${statement.statement_id}`,
              file_id: statement.file_id,
              page: primaryPage,
              pageDisplay,
              document_type: 'bank_statement',
              from_name: statement.bank_name,
              to_name: undefined,
              date: statement.statement_date,
              account: statement.account_number,
              beginning_balance: statement.beginning_balance,
              additions: statement.total_credits,
              deductions: statement.total_debits,
              ending_balance: statement.ending_balance,
              extracted_date: statement.created_at,
              source_data: statement,
              file_name: statement.filename || statement.file_id,
            });
          });
        } else {
          console.warn('Failed to load bank statements:', statementsResult.reason);
        }

        if (checkImagesResult.status === 'fulfilled') {
          const checkImages: CheckImageType[] = checkImagesResult.value.data.check_images || [];
          checkImages.forEach((checkImage) => {
            allDocuments.push({
              id: `check_${checkImage.check_image_id || checkImage.image_id}`,
              file_id: checkImage.file_id,
              page: checkImage.page_number,
              document_type: 'check_image',
              from_name: checkImage.bank_name,
              to_name: checkImage.payee,
              date: checkImage.date,
              account: checkImage.account_number,
              amount: checkImage.amount,
              extracted_date: checkImage.extracted_date || checkImage.created_at,
              source_data: checkImage,
              file_name: checkImage.filename || checkImage.file_id,
            });
          });
        } else {
          console.warn('Failed to load check images:', checkImagesResult.reason);
        }

        allDocuments.sort((a, b) => {
          if (a.file_id !== b.file_id) return a.file_id.localeCompare(b.file_id);
          return (a.page || 0) - (b.page || 0);
        });

        setDocuments(allDocuments);
      } catch (err: any) {
        console.error('Error loading documents:', err);
        setError('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      loadDocuments();
    }
  }, [caseId]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
        <div className="mt-2 small">Loading documents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mt-3">
        {error}
      </Alert>
    );
  }

  if (documents.length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        No documents found for this case.
      </Alert>
    );
  }

  return (
    <div className="mt-4">
      {/* Header with document count */}
      <Row className="mb-3">
        <Col>
          <h5>
            Case Documents ({table.getFilteredRowModel().rows.length} total)
          </h5>
        </Col>
      </Row>

      {/* Global search */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search all columns..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            {globalFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => setGlobalFilter('')}
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setColumnFilters([]);
              setGlobalFilter('');
              setSorting([]);
            }}
          >
            Clear All Filters
          </Button>
        </Col>
      </Row>

      {/* Pagination controls - Top */}
      <Row className="mb-3">
        <Col md={6} className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            ««
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            «
          </Button>
          <span className="small">
            Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
            <strong>{table.getPageCount()}</strong>
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            »
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            »»
          </Button>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end gap-2">
          <span className="small">Show:</span>
          <Form.Select
            size="sm"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            style={{ width: 'auto' }}
          >
            {[10, 25, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize} rows
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped hover size="sm">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} style={{ minWidth: '120px' }}>
                    {/* Column header with sorting */}
                    <div 
                      style={{ 
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.getCanSort() && (
                        <span style={{ fontSize: '0.8em', opacity: 0.6 }}>
                          {{
                            asc: '↑',
                            desc: '↓',
                          }[header.column.getIsSorted() as string] ?? '↕'}
                        </span>
                      )}
                    </div>
                    
                    {/* Column filter */}
                    {header.column.getCanFilter() && (
                      <div className="mt-1">
                        {header.column.columnDef.meta?.filterComponent ? (
                          header.column.columnDef.meta.filterComponent(header.column)
                        ) : (
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder={`Filter ${header.column.columnDef.header}...`}
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value)}
                          />
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* No results message */}
      {table.getFilteredRowModel().rows.length === 0 && (globalFilter || columnFilters.length > 0) && (
        <Alert variant="warning" className="mt-3">
          No documents match your current filters.
        </Alert>
      )}

      {/* Pagination controls - Bottom */}
      <Row className="mt-3">
        <Col md={6} className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            ««
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            «
          </Button>
          <span className="small">
            Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
            <strong>{table.getPageCount()}</strong>
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            »
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            »»
          </Button>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end gap-2">
          <span className="small">Show:</span>
          <Form.Select
            size="sm"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            style={{ width: 'auto' }}
          >
            {[10, 25, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize} rows
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
    </div>
  );
};

export default DocumentsTable;